import { ArrowLeftRight, Info, X } from 'lucide-react';
import { BenchmarkSearchPopover } from '../../components/Benchmark/BenchmarkSearchPopover';
import type { Universe } from '@biasafe.models';
import { cn } from '@/lib/utils';
import type { useBenchmarkSelection } from '../../hooks';

interface BenchmarkSelectionProps {
  benchmark: ReturnType<typeof useBenchmarkSelection>;
}

export function BenchmarkSelection({ benchmark }: BenchmarkSelectionProps) {
  const {
    availableBenchmarks,
    selectedBenchmarkDetails,
    defaultBenchmarkId,
    addBenchmark,
    removeBenchmark,
  } = benchmark;

  return (
    <div className="space-y-2 pt-3 border-t border-border/20">
      {/* Header — matches Figma's w-7 h-7 rounded-lg icon container
          paired with a font-medium text-[16px] title and text-[14px]
          muted description. Icon switched from Shuffle to ArrowLeftRight
          per Figma. */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <ArrowLeftRight className="w-3.5 h-3.5 text-primary" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-[16px]">Benchmark Selection</h4>
          <p className="text-muted-foreground text-[14px]">
            For performance comparison & metric calculation
          </p>
        </div>
      </div>

      {/* Search Popover */}
      <BenchmarkSearchPopover
        availableBenchmarks={availableBenchmarks}
        onSelect={addBenchmark}
      />

      {/* Selected benchmark chips — Figma: purple-tinted rounded-md chips,
          deeper tint for the universe's "linked" default benchmark; neutral
          mono code badge; 15px name; remove button only on user-added rows. */}
      {selectedBenchmarkDetails.length > 0 ? (
        <div className="space-y-1.5">
          {selectedBenchmarkDetails.map((universe: Universe) => {
            const isLinked = universe.id === defaultBenchmarkId;
            // Show the Morningstar benchmark code (e.g. MSUTMETU), not the raw
            // portfolio_id; fall back to the id for any unmapped universe.
            const benchmarkCode =
              universe.benchmarkCode || universe.id;
            // Show the official index name (with "Index"), matching the
            // Universe section; fall back to the short name when unmapped.
            const benchmarkName =
              universe.officialName || universe.name;
            return (
              <div
                key={universe.id}
                className={cn(
                  'flex items-center justify-between px-2.5 py-2 rounded-md border',
                  isLinked
                    ? 'border-primary/30 bg-primary/[0.04]'
                    : 'border-primary/20 bg-primary/[0.02]',
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-flex items-center justify-center h-5 min-w-[84px] px-1.5 rounded border border-border font-mono text-[14px] text-foreground shrink-0">
                    {benchmarkCode}
                  </span>
                  <span className="truncate text-[15px]">{benchmarkName}</span>
                </div>
                {!isLinked && (
                  <button
                    type="button"
                    onClick={() => removeBenchmark(universe.id)}
                    className="inline-flex items-center justify-center h-6 w-6 rounded-md text-muted-foreground hover:bg-primary/10 hover:text-foreground transition-colors shrink-0"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground bg-muted/30 rounded-md p-3 text-center">
          <Info className="w-4 h-4 inline mr-2" />
          No benchmarks selected.
        </div>
      )}
    </div>
  );
}
